import { EditorContent, useEditor } from '@tiptap/react';
import { Highlight } from '@tiptap/extension-highlight';
import { StarterKit } from '@tiptap/starter-kit';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { Typography } from '@tiptap/extension-typography';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { MaybeNil } from '../../../utils/interfaces';
import { useOnClickOutside } from '../../../utils/hooks';
import { theme } from '../../../styles/theme';
import styled from 'styled-components';
import { colors, layout } from '../../../config/styles';
import { EMPTY_RICH_TEXT_CONTENT } from '../../RichTextEditor';
import {
  LinkMenu,
  LinkMenuMode,
} from '../../RichTextEditor/components/LinkMenu';
import { FontSize } from '../../RichTextEditor/extentions/FontSize';
import { FloatingMenu } from '../../RichTextEditor/components/FloatingMenu';
import AddScratchpadContent, {
  ContentType,
} from './AddScratchpadContent/AddScratchpadContent';
import { CompactFormattingMenu } from './CompactFormattingMenu';
import { ContentSplitResult, useContentSplit } from './useContentSplit';
import { findAddMenuPosition, isStartOfEmptyLine } from './utils';

export const extensions = [
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3],
    },
    // Not supported (yet)
    blockquote: false,
    code: false,
    codeBlock: false,
    strike: false,
  }),
  Underline,
  Highlight,
  TextStyle,
  FontSize,
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    // we don't support justify
    alignments: ['left', 'center', 'right'],
  }),
  Link.extend({
    // This setting is required to mark boundaries (start or end of the link)
    // non-inclusive to the link. There are 2 reasons:
    // 1. You can "escape" the link by continue typing (see the video in
    // https://github.com/ueberdosis/tiptap/issues/3293)
    // 2. The Link menu visibility is controlled whether you are inside a link or now
    // Moving the cursor to the end of the link is the way to close the menu after save/cancel in the menu
    inclusive: false,
  }).configure({
    defaultProtocol: 'https',
    openOnClick: false,
    HTMLAttributes: {
      target: '_blank',
    },
  }),
  Typography,
];

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledEditor = styled(EditorContent)`
  width: 100%;
  border-radius: ${layout.containerBorderRadius}px;
  background-color: ${theme.colors.level1};

  .ProseMirror:focus {
    outline: none;
  }

  .tiptap {
    min-height: 16px;

    // Base style
    font-size: 12px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    color: ${theme.colors.secondary900};

    p {
      margin: 0;
      padding: 0;
    }

    p:not(:last-child) {
      margin-bottom: 8px;
    }

    h1 {
      font-size: 32px;
      line-height: 1.5;
      font-weight: 600;
      margin: 0;
      padding: 0;
    }

    h2 {
      font-size: 20px;
      line-height: 1.8;
      font-weight: 600;
      margin: 0;
      padding: 0;
    }

    a {
      color: ${theme.colors.mainBlue};
      text-decoration: underline;
    }

    mark {
      background-color: ${colors.contentRemoveHighlight};
    }
  }
`;

const AddContentButton = styled.div`
  position: absolute;
  transform: translateY(-50%);
  z-index: 100;
`;

export interface IRichTextEditorRef {
  focus: () => void;
  select: () => void;
}

export interface IRichTextEditorProps {
  value: MaybeNil<string>;
  noteId: string;
  onChange?: (value: string, noteId?: string) => void;
  handleAddContent?: (type: ContentType, split?: ContentSplitResult) => void;
  autoFocus?: boolean;
  onFocus?: () => void;
  isFocused?: boolean;
}

export const ScratchpadRichTextEditor = forwardRef<
  IRichTextEditorRef,
  IRichTextEditorProps
>(
  (
    {
      value,
      noteId,
      onChange,
      handleAddContent,
      autoFocus,
      isFocused: externalIsFocused = false,
      onFocus,
    },
    ref,
  ) => {
    const editorRef = useRef<ReturnType<typeof useEditor>>(null);
    const linkMenuRef = useRef<HTMLDivElement>(null);
    const editorUpdated = useRef(false);

    const [isInternalFocused, setInternalFocused] = useState(false);
    const [addMenuPosition, setAddMenuPosition] = useState<{
      top: number;
      left: number;
    } | null>(null);
    const [hasSelection, setHasSelection] = useState(false);
    const { splitContent } = useContentSplit();

    const editor = useEditor({
      extensions,
      content: value ?? EMPTY_RICH_TEXT_CONTENT,
      onFocus: () => {
        setInternalFocused(true);
        onFocus?.();
      },
      onBlur: ({ event }) => {
        // Check if the blur event target is inside the LinkMenu
        if (linkMenuRef.current?.contains(event.relatedTarget as Node)) {
          return;
        }

        setInternalFocused(false);
        setAddMenuPosition(null);
      },
      onSelectionUpdate: ({ editor }) => {
        if (!editor) {
          return;
        }

        setHasSelection(!editor.state.selection.empty);
      },

      onTransaction: ({ editor }) => {
        setAddMenuPosition(findAddMenuPosition(editor));
      },
      onUpdate: ({ editor }) => {
        editorUpdated.current = true;
        if (onChange) {
          onChange(editor.getHTML(), noteId);
        }
      },
      autofocus: autoFocus,
      editorProps: {
        handleKeyDown: (_, event) => {
          if (event.key === 'Tab') {
            // This prevents browser tab navigation
            // When this is not prevented the focus is moved to the next
            // RichTextNote down/up in the list
            event.preventDefault();
          }
          return false;
        },
      },
    });

    // Keep a reference to the editor instance
    useEffect(() => {
      if (editor) {
        editorRef.current = editor;
      }
    }, [editor]);

    // Handle external value updates
    useEffect(() => {
      if (editor && !editorUpdated.current && value !== editor.getHTML()) {
        editor.commands.setContent(value ?? '');
      }
      editorUpdated.current = false;
    }, [value, editor]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editor?.commands.focus(),
        select: () => editor?.commands.selectAll(),
      }),
      [editor],
    );

    const shouldShowFormattingMenu =
      (editor &&
        (hasSelection ||
          editor.isActive(['bold', 'italic', 'underline', 'highlight']))) ??
      false;

    const shouldShowEditLinkMenu =
      (editor &&
        isInternalFocused &&
        editor.state.selection.empty &&
        editor.isActive('link')) ??
      false;

    const containerRef = useRef<HTMLDivElement>(null);
    const handleTriggerOnChange = () => {
      if (onChange && editor) {
        onChange(editor.getHTML());
      }

      if (isInternalFocused) {
        setInternalFocused(false);
      }
    };

    useOnClickOutside(containerRef, handleTriggerOnChange);

    useEffect(() => {
      if (autoFocus && editor) {
        setTimeout(() => {
          editor.commands.focus();
          setInternalFocused(true);
        }, 0);
      }
    }, [editor, autoFocus]);

    const isFocused = isInternalFocused || externalIsFocused;

    const showAddButton =
      !!editor && isFocused && !hasSelection && isStartOfEmptyLine(editor);

    const handleAddContentWithSplit = (type: ContentType) => {
      if (!editor || !handleAddContent) {
        return;
      }
      splitContent(editor, type, handleAddContent);
    };

    return (
      <Wrapper ref={containerRef}>
        {editor && (
          <>
            <FloatingMenu
              editor={editor}
              open={shouldShowFormattingMenu && isFocused}
            >
              <CompactFormattingMenu editor={editor} />
            </FloatingMenu>
            <FloatingMenu
              editor={editor}
              open={shouldShowEditLinkMenu && isFocused}
            >
              <div ref={linkMenuRef}>
                <LinkMenu editor={editor} mode={LinkMenuMode.Edit} />
              </div>
            </FloatingMenu>
            {handleAddContent && showAddButton && (
              <AddContentButton
                style={{
                  top: `${addMenuPosition?.top}px`,
                  left: `${addMenuPosition?.left}px`,
                }}
              >
                <AddScratchpadContent onAdd={handleAddContentWithSplit} />
              </AddContentButton>
            )}
          </>
        )}
        <StyledEditor editor={editor} />
      </Wrapper>
    );
  },
);
